import React, { useMemo } from 'react'
import { useMediaQuery } from 'react-responsive'
import { DataTable } from 'shared/components/DataTable/DataTable'
import { ReactComponent as StudentsIcon } from 'assets/icons/people.svg'
import s from './AssignmentTable.module.scss'

const formatPercent = value => {
	const n = Number(value)
	if (Number.isNaN(n)) return '—'
	return `${Math.round(n)}%`
}

const mkSortOptions = (key, labels) => [
	{ id: `${key}_asc`, key, dir: 'asc', label: labels?.asc || 'Ascending' },
	{ id: `${key}_desc`, key, dir: 'desc', label: labels?.desc || 'Descending' },
]

const AssignmentTable = ({
	rows = [],
	sortKey,
	sortDir,
	onSortChange,
	onRowClick,
}) => {
	const isMobile = useMediaQuery({ query: '(max-width:1199px)' })

	const columns = useMemo(
		() => [
			{
				id: 'name',
				label: 'Assignment Name',
				stickyLeft: 0,
				width: isMobile ? '170px' : '360px',
				sortable: true,
				isSortActive: sortKey === 'name',
				sortDir,
				sortOptions: mkSortOptions('name', { asc: 'A → Z', desc: 'Z → A' }),
				onSortSelect: opt => onSortChange?.({ opt, name: 'Name' }),
				cellClassName: s.colNameCell,
				render: row => (
					<div
						className={s.activity}
						onClick={() => onRowClick?.(row)}
						style={{ cursor: onRowClick ? 'pointer' : 'default' }}
					>
						<div className={s.activity__content}>
							<span className={s.activity__title}>
								{row?.name || 'Assignment'}
							</span>
							{row?.gradeLabel ? (
								<span className={s.activity__grade}>{row.gradeLabel}</span>
							) : null}
						</div>
						{row?.newStudentsCount ? (
							<span className={s.newBadge}>{row.newStudentsCount} new</span>
						) : null}
					</div>
				),
			},
			{
				id: 'status',
				label: 'Status',
				width: isMobile ? '90px' : '120px',
				sortable: true,
				isSortActive: sortKey === 'status',
				sortDir,
				sortOptions: mkSortOptions('status', { asc: 'A → Z', desc: 'Z → A' }),
				onSortSelect: opt => onSortChange?.({ opt, name: 'Status' }),
				cellClassName: s.colStatusCell,
				render: row => {
					const isCompleted =
						String(row?.status || '').toLowerCase() === 'completed'
					return (
						<span
							onClick={() => onRowClick?.(row)}
							className={`${s.tag} ${
								isCompleted ? s.tagCompleted : s.tagActive
							}`.trim()}
						>
							{isCompleted ? 'Completed' : 'Active'}
						</span>
					)
				},
			},
			{
				id: 'studentsCount',
				label: 'Students',
				width: isMobile ? '80px' : '120px',
				sortable: true,
				isSortActive: sortKey === 'students',
				sortDir,
				sortOptions: mkSortOptions('students', {
					asc: 'Low → High',
					desc: 'High → Low',
				}),
				onSortSelect: opt => onSortChange?.({ opt, name: 'Students' }),
				cellClassName: s.colStudentsCell,
				render: row => (
					<div className={s.students} onClick={() => onRowClick?.(row)}>
						<span className={s.students__icon} aria-hidden="true">
							<StudentsIcon />
						</span>
						<span className={s.students__text}>
							{row?.studentsCount ?? '—'}
						</span>
					</div>
				),
			},
			{
				id: 'completion',
				label: 'Completion',
				width: isMobile ? '120px' : '160px',
				sortable: true,
				isSortActive: sortKey === 'completion',
				sortDir,
				sortOptions: mkSortOptions('completion', {
					asc: 'Low → High',
					desc: 'High → Low',
				}),
				onSortSelect: opt => onSortChange?.({ opt, name: 'Completion' }),
				cellClassName: s.colCompletionCell,
				render: row => {
					const n = Number(row?.completion)
					const val = Number.isNaN(n) ? 0 : Math.max(0, Math.min(100, n))
					const colorFill = val >= 80 ? 'success' : val >= 60 ? 'medium' : 'bed'
					return (
						<div className={s.progress} onClick={() => onRowClick?.(row)}>
							<span className={s.progress__text}>{formatPercent(val)}</span>
							<div className={s.progress__bar}>
								<div
									className={`${s.progress__fill} ${s[colorFill]}`.trim()}
									style={{ width: `${val}%` }}
								/>
							</div>
						</div>
					)
				},
			},
			{
				id: 'created',
				label: 'Created',
				width: isMobile ? '100px' : '120px',
				sortable: true,
				isSortActive: sortKey === 'created',
				sortDir,
				sortOptions: mkSortOptions('created', {
					asc: 'Oldest → Newest',
					desc: 'Newest → Oldest',
				}),
				onSortSelect: opt => onSortChange?.({ opt, name: 'Created' }),
				cellClassName: s.colUpdatedCell,
				render: row => (
					<span className={s.lastUpd} onClick={() => onRowClick?.(row)}>
						{row?.created || '—'}
					</span>
				),
			},
			{
				id: 'lastUpdated',
				label: 'Last Updated',
				width: isMobile ? '110px' : '140px',
				sortable: true,
				isSortActive: sortKey === 'lastUpdated',
				sortDir,
				sortOptions: mkSortOptions('lastUpdated', {
					asc: 'Oldest → Newest',
					desc: 'Newest → Oldest',
				}),
				onSortSelect: opt => onSortChange?.({ opt, name: 'Last Updated' }),
				cellClassName: s.colUpdatedCell,
				render: row => (
					<span className={s.lastUpd} onClick={() => onRowClick?.(row)}>
						{row?.lastUpdated || '—'}
					</span>
				),
			},
		],
		[isMobile, onRowClick, onSortChange, sortDir, sortKey],
	)

	return (
		<div className={s.wrap}>
			<DataTable
				columns={columns}
				rows={rows}
				getRowId={row => row.id}
				onRowClick={onRowClick}
				stickyHeader
				minWidth={isMobile ? 700 : 960}
				layout={isMobile ? 'fixed' : 'auto'}
				emptyText="No new assignment updates"
				variant="light"
			/>
		</div>
	)
}

export default AssignmentTable
